import { useState, useEffect, useCallback } from 'preact/hooks'
import { swymService } from 'libs/swym/swym-service'
import { globalEmitter, emitterProps } from 'helpers/emitter-event'

/**
 * Heart toggle rendered top-right of product cards.
 * Two modes, chosen by whether `entry` is passed:
 * - Without `entry` (PLP, search, recs, shop-the-look): adds the first
 *   available variant; state is per-card, keyed by product id, and tracks the
 *   `wishlist.change` event.
 * - With `entry` (wishlist page): the card already represents a saved entry,
 *   so `saved` is constant and a click removes exactly that `{empi, epi, du}`.
 */
export function WishlistCardButton({ result, className = '', entry = null }) {
  // `uid` is Athos's per-SKU identifier — Swym needs the actual Shopify product
  // id (`parentId`, hoisted from `mappings.core` on recs, present top-level on
  // Discovery too) to recognise the item, or it rejects with `sw-unknown-product`.
  // On the wishlist page the entry itself carries the ids, so it wins.
  const empi = Number(entry?.empi ?? result.parentId ?? result.uid)
  // An entry-scoped button only ever renders for something already saved, and
  // it removes that one entry — so its state is constant.
  const [saved, setSaved] = useState(entry ? true : swymService.has(empi))
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (entry) return
    const handler = (e) => setSaved(e.detail.empis.includes(empi))
    globalEmitter.on(emitterProps.wishlist.change, handler)
    return () => globalEmitter.off(emitterProps.wishlist.change, handler)
  }, [empi, entry])

  const toggle = useCallback(async () => {
    if (busy) return
    setBusy(true)
    if (entry) {
      // Wishlist page: remove exactly this size's entry.
      await swymService.removeVariant(entry)
    } else if (saved) {
      // No size context on this surface, so clear every saved size.
      await swymService.removeProduct(empi)
    } else if (result.variants?.length) {
      const variant = result.variants.find((v) => v.inventory_quantity > 0) ?? result.variants[0]
      await swymService.add({
        epi: Number(variant.id),
        empi,
        du: `${window.location.origin}/products/${result.handle}`
      })
    }
    setBusy(false)
  }, [busy, saved, empi, result, entry])

  return (
    <div className={`swym-card-button absolute right-0 top-0 z-30 flex items-center justify-center p-0.5 lg:p-1 ${className}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-label={saved ? 'Remove from Wishlist' : 'Add to Wishlist'}
        aria-pressed={saved}
        className="aspect-1/1 size-[18px] children:size-full text-type-black-primary lg:size-20px group cursor-pointer"
      >
        {/* fill is class-driven: a fill-* utility always beats the svg fill attribute */}
        <svg class="fill-transparent group-hover:fill-brand-black group-aria-pressed:fill-brand-black transition-colors duration-300 pointer-events-none" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.9072 4.11328C14.9737 3.54548 16.2039 3.36465 17.3887 3.60156C18.5736 3.83855 19.6401 4.4786 20.4062 5.41309C21.1724 6.34761 21.5908 7.51912 21.5908 8.72754C21.5907 12.0485 19.1665 14.9957 16.8799 17.0537C15.7216 18.0961 14.5656 18.9397 13.7002 19.5225C13.2669 19.8142 12.9047 20.0415 12.6504 20.1963C12.5233 20.2736 12.4227 20.3328 12.3535 20.373C12.3193 20.393 12.2928 20.4084 12.2744 20.4189C12.2652 20.4242 12.2579 20.4288 12.2529 20.4316L12.2451 20.4355V20.4365H12.2441L12 20.5732L11.7559 20.4365L11.7549 20.4355L11.7471 20.4316C11.7421 20.4289 11.7356 20.4241 11.7266 20.4189C11.7081 20.4084 11.6811 20.3932 11.6465 20.373C11.5773 20.3327 11.4767 20.2737 11.3496 20.1963C11.0954 20.0416 10.734 19.8141 10.3008 19.5225C9.43528 18.9397 8.27857 18.0963 7.12012 17.0537C4.83345 14.9957 2.40931 12.0485 2.40918 8.72754C2.40918 7.51913 2.82764 6.3476 3.59375 5.41309C4.35987 4.47857 5.42638 3.83855 6.61133 3.60156C7.79627 3.36458 9.02713 3.54531 10.0938 4.11328C10.8691 4.5262 11.5222 5.1258 12 5.85059C12.4779 5.12568 13.1318 4.52621 13.9072 4.11328Z"
            stroke="currentColor"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>
  )
}
